import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Award } from 'lucide-react';
import { fmtUSD } from './utils';
import logger from '../../utils/logger';

const EMPTY = { name: '', funder: '', award_amount: '', spent_amount: '', start_date: '', end_date: '', status: 'active', report_due_date: '', notes: '' };

const STATUS_STYLES = {
  active: 'bg-[#E8F5E9] text-[#1B5E20] border-[#81C784]',
  completed: 'bg-[#F0EDF6] text-[#5A4D6E] border-[#DED8EB]',
  pending: 'bg-[#FFF3E0] text-[#E65100] border-[#FFB74D]',
  declined: 'bg-red-50 text-[#D9534F] border-red-300',
};

export default function GrantsTab({ api }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchAll = useCallback(async () => {
    try { const { data } = await api.get('/finance/grants'); setList(data); } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...EMPTY, ...g }); setDialog(true); };

  const save = async () => {
    try {
      if (!form.name) { toast.error('Name required'); return; }
      if (editing) { await api.put(`/finance/grants/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/finance/grants', form); toast.success('Saved'); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this grant?')) return;
    await api.delete(`/finance/grants/${id}`); fetchAll();
  };

  const totals = {
    awarded: list.filter(g => g.status === 'active').reduce((s, g) => s + (g.award_amount || 0), 0),
    spent: list.filter(g => g.status === 'active').reduce((s, g) => s + (g.spent_amount || 0), 0),
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-3">
          <Card className="border-[#81C784] bg-[#E8F5E9]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#1B5E20] font-semibold">Active Awarded</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.awarded)}</p></CardContent></Card>
          <Card className="border-[#DED8EB] bg-white"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#8E85A0] font-semibold">Active Remaining</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.awarded - totals.spent)}</p></CardContent></Card>
        </div>
        <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-9" data-testid="grant-add-btn"><Plus className="h-4 w-4" /> Add Grant</Button>
      </div>

      {loading ? <p className="text-sm text-[#8E85A0]">Loading…</p> : list.length === 0 ? (
        <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
          <Award className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No grants tracked.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="grant-list">
          {list.map(g => (
            <Card key={g.id} className="border shadow-sm bg-white" data-testid={`grant-row-${g.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#2D1B4E]">{g.name}</h3>
                      <Badge className={`text-[10px] border ${STATUS_STYLES[g.status] || STATUS_STYLES.pending}`}>{g.status}</Badge>
                      {g.funder && <span className="text-[11px] text-[#8E85A0]">· {g.funder}</span>}
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px] text-[#5A4D6E]">
                      <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{fmtUSD(g.spent_amount)} / {fmtUSD(g.award_amount)}</span>
                      <span className="text-[10px]">· {g.pct_used}% used</span>
                      {g.end_date && <span className="text-[10px]">· ends {g.end_date}</span>}
                      {g.report_due_date && <span className="text-[10px] text-[#E65100]">· report due {g.report_due_date}</span>}
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-[#F0EDF6] overflow-hidden mt-1">
                      <div className="h-full bg-[#6B3FA0] transition-all" style={{ width: `${Math.min(100, g.pct_used || 0)}%` }} />
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(g)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`grant-edit-${g.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(g.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`grant-del-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="grant-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">{editing ? 'Edit Grant' : 'New Grant'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Grant Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="border-[#DED8EB]" data-testid="grant-name" /></div>
            <div><Label>Funder</Label><Input value={form.funder} onChange={e => setForm(f => ({ ...f, funder: e.target.value }))} placeholder="Foundation / agency" className="border-[#DED8EB]" data-testid="grant-funder" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Award Amount ($)</Label><Input type="number" step="0.01" value={form.award_amount} onChange={e => setForm(f => ({ ...f, award_amount: e.target.value }))} className="border-[#DED8EB]" data-testid="grant-award" /></div>
              <div><Label>Spent ($)</Label><Input type="number" step="0.01" value={form.spent_amount} onChange={e => setForm(f => ({ ...f, spent_amount: e.target.value }))} className="border-[#DED8EB]" data-testid="grant-spent" /></div>
              <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="border-[#DED8EB]" data-testid="grant-start" /></div>
              <div><Label>End</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="border-[#DED8EB]" data-testid="grant-end" /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="grant-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{['active', 'pending', 'completed', 'declined'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Report Due</Label><Input type="date" value={form.report_due_date} onChange={e => setForm(f => ({ ...f, report_due_date: e.target.value }))} className="border-[#DED8EB]" data-testid="grant-report" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="border-[#DED8EB]" data-testid="grant-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="grant-save-btn">{editing ? 'Update' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
